import { Badge, Tag, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import type { OperationStatus } from '@/shared/api/operationResult'

/** Abonent holati — 01-DOMAIN-MODEL.md §2 dagi holat mashinasi */
export type SubscriberState = 'AVAILABLE' | 'NEW' | 'ACTIVE' | 'SUSPENDED' | 'BLOCKED' | 'CLOSED'

/**
 * Rang holatning SABABINI ko'rsatadi, og'irligini emas:
 *   · `SUSPENDED` — sariq, chunki bu TIZIM qarori (pul tugadi) va to'lovdan
 *     keyin o'zi tiklanadi
 *   · `BLOCKED` — qizil, chunki bu ODAM qarori va faqat odam ochadi
 * Ikkalasini bitta rangda ko'rsatish "nega o'zi ochilib ketdi?" degan
 * savolga olib keladi.
 */
const STATE_COLOR: Record<SubscriberState, string> = {
  AVAILABLE: 'blue',
  NEW: 'default',
  ACTIVE: 'green',
  SUSPENDED: 'gold',
  BLOCKED: 'red',
  CLOSED: 'default',
}

const STATE_HINT: Partial<Record<SubscriberState, string>> = {
  AVAILABLE: 'subscriberState.availableHint',
  SUSPENDED: 'subscriberState.suspendedHint',
  BLOCKED: 'subscriberState.blockedHint',
}

export function SubscriberStateTag({ state }: { state: SubscriberState }) {
  const { t } = useTranslation()
  const hintKey = STATE_HINT[state]
  const tag = <Tag color={STATE_COLOR[state]}>{t(`subscriberState.${state}`)}</Tag>

  return hintKey ? <Tooltip title={t(hintKey)}>{tag}</Tooltip> : tag
}

/**
 * Ulanish holati — abonent holatidan BOSHQA narsa.
 *
 * `ACTIVE` abonent hozir offline bo'lishi mumkin (router o'chirilgan), va
 * aksincha: grant tugagan, lekin sessiya hali uzilmagan (CoA yetib bormagan)
 * — 01-DOMAIN-MODEL.md §1, "AccessGrant ≠ Session". Shuning uchun ikkita
 * alohida komponent, bitta ustunga birlashtirilmaydi.
 */
export function OnlineBadge({ online }: { online: boolean }) {
  const { t } = useTranslation()
  return (
    <Badge
      status={online ? 'success' : 'default'}
      text={online ? t('status.online') : t('status.offline')}
    />
  )
}

/**
 * `OperationResult.status` uchun tag — `OperationResultAlert` bilan BIR XIL
 * rang qoidasi (CLAUDE.md §3.2, Qoida 4): `PARTIAL` xato emas, ogohlantirish.
 * Audit jurnalida har qator shu uchtadan biri, shuning uchun operator
 * ro'yxatni ko'zdan kechirganda rangdan darhol "bu qatorga qarash kerakmi"
 * degan xulosaga kelishi kerak.
 */
const OPERATION_STATUS_COLOR: Record<OperationStatus, string> = {
  OK: 'green',
  PARTIAL: 'gold',
  FAILED: 'red',
}

export function OperationStatusTag({ status }: { status: OperationStatus }) {
  const { t } = useTranslation()
  return (
    <Tooltip title={t(`audit.statusHint.${status}`)}>
      <Tag color={OPERATION_STATUS_COLOR[status]}>{t(`audit.status.${status}`)}</Tag>
    </Tooltip>
  )
}
